<script setup>
import { ref, watch, onMounted, reactive, computed } from 'vue';
import { useToast } from "primevue/usetoast";
import { useMainStore } from '../stores/store.js';
import { useRouter } from 'vue-router';
import { callAPI, DRUPAL_API_ROUTE } from "@/service/DrupalService";
import { useForm } from 'vee-validate';
import * as yup from 'yup';
import { useI18n } from 'vue-i18n'
import DomainConfigurator from '@/components/DomainConfigurator.vue';
import WebformTemplatePicker from '@/components/WebformTemplatePicker.vue';

const { t } = useI18n();

const schema = yup.object({
  title: yup.string().required().label('Title'),
  domain: yup.string().required(),
  customPath: yup.string().min(4).matches(/^[^#%^*\[\]{};':"\\|,.<>/?]*$/).label('customPath'),
  gdpr: yup.boolean().required().oneOf([true], t('validation.required'))
});

const { defineField, handleSubmit, resetForm, errors, setFieldError } = useForm({
  validationSchema: schema,
});

const [title] = defineField('title');
const [domain] = defineField('domain');
const [customPath] = defineField('customPath');
const [gdpr] = defineField('gdpr');

const toast = useToast();

const props = defineProps({
  visible: {
    type: Boolean,
    required: true
  }
});
const emit = defineEmits(['update:visible']);

const localVisible = computed({
  get: () => props.visible,
  set: (value) => emit('update:visible', value)
});

const store = useMainStore();
const router = useRouter();

const user = ref(JSON.parse(localStorage.getItem('user')));

const addDomainDialog = ref(false);

// Starting point of the new webform: a blank form, or a template whose
// questions are added once the webform is created.
const startMode = ref('blank');
const startTemplate = ref(null);
const templatePicker = ref(false);

const chooseBlank = () => {
  startMode.value = 'blank';
  startTemplate.value = null;
}

const chooseTemplates = () => {
  startMode.value = 'template';
  templatePicker.value = true;
}

// Closing the picker without a template goes back to the blank form.
const closeTemplatePicker = (visible) => {
  templatePicker.value = visible;
  if (!visible && !startTemplate.value) startMode.value = 'blank';
}

const initialState = {
  webformLoading: false,
  domainsLoading: false,
  webformToggle: true,
  webformLogoToggle: false,
  webformLinksToggle: false,
  title: '',
  customPath: '',
  customPathDisabled: false,
  customPathInvalid: false,
  customPathStatus: '',
  customPathInputLoading: false,
  uuid: '',
  domain: '11111111-1111-1111-1111-111111111111',
  domainsList: [],
  domainDisabled: false,
  description: ''
};

const formData = reactive({ ...initialState });

const home = ref({
  icon: 'pi pi-home'
});
const items = ref([
  {
    label: 'Form'
  }
]);

const fetchDomains = async (refresh = false, uuid = false) => {
  domain.value = formData.domain;
  var currentWorkspace = user.value.current_workspace;
  var permission = user.value.permissions.crud_webforms;
  if (permission) {
    if (refresh) {
      formData.domainsLoading = true;
      var response = await callAPI(DRUPAL_API_ROUTE.WORKSPACES_DOMAINS_LIST, { workspace: currentWorkspace, type: 'webforms' });
      formData.domainsList = response.data;
      formData.domainsLoading = false;
    } else {
      formData.domainsList = JSON.parse(localStorage.getItem('webformsDomains'));
    }

    var workspaceData = JSON.parse(localStorage.getItem('workspaces'));
    var currentWorkspaceData = workspaceData.find(item => item.id === currentWorkspace);
    var defaultDomain = currentWorkspaceData?.default_domain;
    if (defaultDomain && formData.domainsList?.some(item => item.uuid === defaultDomain)) domain.value = defaultDomain;
    if (uuid) domain.value = uuid;

  } else {
    formData.domainsList = [];
    formData.domainDisabled = true;
  }

};

async function customPathInput() {
  formData.customPath = customPath.value;

  formData.customPathInvalid = false;
  formData.customPathStatus = t('webform.custom_path.validation');

  if (errors.value.customPath) return;

  if (formData.customPath.length < 4) return;
  formData.customPathInputLoading = true;

  try {
    var response = await callAPI(DRUPAL_API_ROUTE.WEBFORM_ALIAS, {}, { 'alias': [formData.customPath] });
    if (response.data.available) {
      if (formData.customPath.match(/[^\u0000-\u007F]/g)) {
        formData.customPathStatus = t('webform.custom_path.available_nonascii');
      } else {
        formData.customPathStatus = t('webform.custom_path.available');
      }
    } else {
      formData.customPathInvalid = true;
      setFieldError('customPath', t('webform.custom_path.errors.duplicate'), false);
    }
  } catch (err) {
    formData.customPathStatus = t('webform.custom_path.errors.general');
  } finally {
    formData.customPathInputLoading = false;
  }
}

const onSubmit = handleSubmit((values) => {
  if (formData.customPathInvalid) return;
  formData.title = values.title;
  formData.domain = values.domain;
  formData.customPath = values.customPath;
  var update = (formData.uuid) ? true : false;
  generateWebform(update);
});

const generateWebform = async (update = false) => {
  if (formData.generateLoading) return;

  formData.generateLoading = true;

  var data = {
    "title": formData.title,
    "alias": formData.customPath,
    "domain": formData.domain
  };

  if (!update) {
    try {
      var response = await callAPI(DRUPAL_API_ROUTE.WEBFORM_CREATE, {}, data);
      var webform = response.data;
      if (startTemplate.value && webform) {
        var request = await callAPI(DRUPAL_API_ROUTE.WEBFORM_WEBFORM_TEMPLATE, { uuid: webform.uuid, id: webform.webform }, { template: startTemplate.value.id });
        if (request.status == 200) {
          webform.fromTemplate = true;
        } else {
          toast.add({ severity: 'error', summary: t('common.labels.error'), detail: t('webform.start.error'), life: 5000 });
        }
      }
    } catch (err) {
      console.log(err);
    } finally {
      var result = webform;
      var alias = formData.domainsList.find(item => item.uuid === formData.domain);
      result.alias = 'https://' + alias.domain + '/f/' + result.path;
      result.isNew = true;

      formData.generateLoading = false;
      localVisible.value = false;
      store.setMessage(result);
      resetFormData();
      router.push({
        path: '/webforms',
        force: true
      });
    }
  } else {
    try {
      var response = await callAPI(DRUPAL_API_ROUTE.WEBFORM_UPDATE, { uuid: formData.uuid }, data);
      var webform = response.data;
    } catch (err) {
      console.log(err);
    } finally {
      formData.generateLoading = false;
      localVisible.value = false;
      resetFormData();
      toast.add({ severity: 'info', summary: t('webform.head'), detail: t('webform.update.success'), life: 5000 });
      router.push({
        path: '/webforms',
        force: true
      });
    }
  }
};

function resetFormData() {
  Object.assign(formData, initialState);
  chooseBlank();
}

async function handleShow() {
  user.value = JSON.parse(localStorage.getItem('user'));
  await fetchDomains();
}

function transformText(text) {
  return text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[`#%^*=\[\]{};':"\\|,.<>\/?()]/g, '')
    .replace(/\s+/g, '-')
    .toLowerCase();
}

function handleHide() {
  resetForm();
  resetFormData();
  emit('update:visible', false);
}

watch(() => formData.title,
  (newValue) => {
    formData.customPath = transformText(newValue)
  }
)
</script>


<template>
  <Fluid>
    <Drawer :blockScroll="true" @hide="handleHide" @show="handleShow" v-model:visible="localVisible"
      class="!w-full md:!w-80 lg:!w-[35rem]">
      <template #header>
        <div class="flex items-center gap-2 text-xs">
          <Breadcrumb :home="home" :model="items" class="!p-0">
            <template #item="{ item }">
              <span v-if="item.label" class="text-primary mr-2">{{ item.label }}</span>
              <span :class="item.icon"></span>
            </template>
            <template #separator><i class="pi pi-angle-right"></i></template>
          </Breadcrumb>
        </div>
      </template>
      <div class="flex mx-auto">
        <div class="flex flex-col w-full">
          <form @submit="onSubmit">
            <div class="flex flex-col gap-2 space-y-4">
              <div class="flex items-center space-x-2">
                <ToggleSwitch v-model="formData.webformToggle" />
                <span class="text-xl">{{ t('webform.head') }}</span><i class="pi pi-file me-auto"></i>
              </div>
              <div v-if="formData.webformToggle" class="space-y-6">
                <div class="flex flex-col gap-4">
                  <div class="flex flex-col gap-2">
                    <label for="title">{{ t('webform.title') }}</label>
                    <Skeleton v-if="formData.webformLoading" height="3rem" class="mb-2">
                    </Skeleton>
                    <InputText v-else id="title" size="large" v-model="title" :class="{ 'p-invalid': errors.title }"
                      :disabled="!user.permissions.crud_webforms" />
                    <Message v-if="errors.title" severity="error" size="small" variant="simple">{{
                      errors.title }}
                    </Message>
                  </div>
                  <div class="flex flex-row flex-nowrap mb-5">
                    <div class="flex flex-col gap-2">
                      <label for="domain">{{ t('webform.domain') }}</label>
                      <InputGroup>
                        <InputGroupAddon>
                          <Button icon="pi pi-refresh" v-tooltip.bottom="t('common.labels.refresh')"
                            severity="secondary" @click="fetchDomains(true)" />
                        </InputGroupAddon>
                        <Select v-model="domain" :options="formData.domainsList" optionValue="uuid" optionLabel="domain"
                          :placeholder="t('common.placeholders.select')"
                          :disabled="formData.domainDisabled || !user.permissions.create_webforms_with_a_custom_domain"
                          :loading="formData.domainsLoading" :class="{ 'p-invalid': errors.domain }" />
                        <Message v-if="errors.domain" severity="error" size="small" variant="simple">{{
                          errors.domain }} </Message>
                      </InputGroup>
                    </div>
                    <div class="basis-1/12 flex-col gap-2 text-center">
                      <span class="text-3xl mt-8 hidden sm:block">/f/</span>
                    </div>
                    <div class="grow flex flex-col gap-2">
                      <label for="custom_path">{{ t('webform.custom_path.title') }}</label>
                      <Skeleton v-if="formData.webformLoading" height="3rem" class="mb-2">
                      </Skeleton>
                      <IconField v-else>
                        <InputGroup>
                          <InputText v-model="customPath" @input="customPathInput"
                            :disabled="formData.customPathDisabled || !user.permissions.create_webforms_with_a_custom_slug"
                            :invalid="formData.customPathInvalid" :class="{ 'p-invalid': errors.customPath }" />
                          <InputIcon v-if="formData.customPathInputLoading" class="pi pi-spin pi-spinner" />
                        </InputGroup>
                      </IconField>
                      <Message v-if="errors.customPath" severity="error" size="small" variant="simple">{{
                        errors.customPath }} </Message>
                      <Message v-if="formData.customPathStatus == t('webform.custom_path.available')" severity="success"
                        size="small" variant="simple"><i class="pi pi-check"></i> {{
                          formData.customPathStatus }}
                      </Message>
                      <Message v-if="formData.customPathStatus == t('webform.custom_path.available_nonascii')"
                        severity="warn" size="small" variant="simple"><i class="pi pi-exclamation-triangle"></i> {{
                          formData.customPathStatus }}
                      </Message>
                    </div>
                  </div>
                  <div v-if="formData.domainsList.length == 1" class="flex flex-col mb-5">
                    <Fieldset :legend="t('webform.custom_domains.title')">
                      <div class="flex items-center justify-between w-full">
                        <div class="w-3/4 pr-2">
                          <p class="mb-0 text-sm">{{ t('webform.custom_domains.content') }}</p>
                        </div>
                        <div class="w-1/2 flex justify-end pl-2">
                          <Button :label="t('dashboard.featured.custom_domains.label')" @click="addDomainDialog = true"
                            size="small" />
                        </div>
                      </div>
                    </Fieldset>
                  </div>
                  <div v-if="!formData.uuid" class="flex flex-col gap-2">
                    <label>{{ t('webform.start.title') }}</label>
                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <button type="button" :disabled="!user.permissions.crud_webforms"
                        class="relative flex flex-col items-start gap-1 p-4 pe-8 rounded border-2 text-left cursor-pointer"
                        :class="startMode === 'blank' ? 'border-primary bg-primary-50' : 'border-surface-200 hover:border-surface-300'"
                        @click="chooseBlank">
                        <i v-if="startMode === 'blank'" class="pi pi-check-circle text-primary absolute top-2 right-2"></i>
                        <span class="font-medium">{{ t('webform.start.blank') }}</span>
                        <span class="text-sm text-muted-color">{{ t('webform.start.blank_description') }}</span>
                      </button>
                      <button type="button" :disabled="!user.permissions.crud_webforms"
                        class="relative flex flex-col items-start gap-1 p-4 pe-8 rounded border-2 text-left cursor-pointer"
                        :class="startMode === 'template' ? 'border-primary bg-primary-50' : 'border-surface-200 hover:border-surface-300'"
                        @click="chooseTemplates">
                        <i v-if="startMode === 'template'" class="pi pi-check-circle text-primary absolute top-2 right-2"></i>
                        <span class="font-medium">{{ t('webform.start.templates') }}</span>
                        <span v-if="startTemplate" class="text-sm text-primary">{{ startTemplate.title }}</span>
                        <span v-else class="text-sm text-muted-color">{{ t('webform.start.templates_description') }}</span>                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="flex flex-col mt-5" v-if="!user.permissions.crud_webforms">
              <Message icon="pi pi-info-circle" severity="info">{{ t('webforms.notallowed') }}
              </Message>
            </div>
          </form>
        </div>
      </div>
      <template #footer>
        <div class="flex items-start gap-2 mb-4">
          <Checkbox v-model="gdpr" inputId="gdpr" binary :disabled="formData.gdprDisabled || !user.permissions.crud_webforms"
            :class="{ 'p-invalid': errors.gdpr }" />
          <div class="flex flex-col">
            <label for="gdpr">{{ t('webform.gdpr') }}</label>
            <Message v-if="errors.gdpr" severity="error" size="small" variant="simple">{{ errors.gdpr }}</Message>
          </div>
        </div>
        <div v-if="!formData.pageLoading" class="flex items-center gap-2">
          <Button @click="handleHide" :label="t('common.labels.cancel')" class="flex-auto" outlined />
          <Button v-if="!formData.uuid" type="submit" @click="onSubmit" :disabled="!user.permissions.crud_webforms"
            :loading="formData.generateLoading" :label="t('common.labels.create')" />
          <Button v-else type="submit" @click="onSubmit" :loading="formData.generateLoading"
            :label="t('common.labels.update')" />
        </div>
      </template>
    </Drawer>
  </Fluid>
  <DomainConfigurator v-model:visible="addDomainDialog"
    @update:visible="(val) => { if (!val) fetchDomains(true) }"
    @domain="(domain) => { if (domain?.id && domain?.status == 1) fetchDomains(true, domain.id) }" />
  <WebformTemplatePicker :visible="templatePicker" @update:visible="closeTemplatePicker" :selected="startTemplate"
    @select="(template) => startTemplate = template" />
</template>
